"use client"

import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { ChevronDown, ChevronUp, Calendar, CreditCard, Building, TrendingUp, TrendingDown, Loader2, FileText, FileCode, Search, Sparkles } from "lucide-react"
import DocumentActionsDropdown, { createCommonActions } from '../../../shared/components/DocumentActionsDropdown'
import {
  uploadBankStatement,
  fetchBankStatements,
  getBankStatement,
  deleteBankStatement,
  downloadBankStatement,
  searchBankStatements,
  fetchFirms,
  FirmSummary,
} from "../../../lib/api/api"
import { useAuth } from "../../../contexts/AuthContext"
import DragDropUpload from "../../../shared/components/DragDropUpload"
import { formatDate as formatDateUtil, formatCurrency } from "../../../shared/utils/format"

type BankStatementDoc = {
  id: string
  title: string
  description: string
  bank_name: string
  account_number: string
  period_start: string | null
  period_end: string | null
  file_name: string
  created_at: string
  transactions_count: number
  // What the delete route would answer for this user (server FirmAccess.can_edit_document)
  can_delete?: boolean
}

type Transaction = {
  id: string
  date: string | null
  description: string
  amount: number | null
  balance: number | null
  category: string | null
  type: string | null
  reference: string | null
}

type BankStatementDetail = BankStatementDoc & {
  transactions: Transaction[]
}

interface UploadResponse {
  id: string
  status: string
  message?: string
  warning?: string
  transactions_count?: number
  chunks_processed?: number
}

// Bank statements belong to the firm: every member reads them. Deleting one
// follows the server's FirmAccess.can_edit_document (the firm admin and the
// professionals any statement, an assistant only their own uploads): each
// statement carries the answer as can_delete, and the delete action is shown
// only when it is true. A 403 (rights changed since the list loaded) still
// shows a translated permissions error (errors.forbidden).
export default function BankStatements() {
  const { t } = useTranslation()
  const { user, isSuperAdmin } = useAuth()

  // The super admin belongs to no firm: they must pick one before the list
  // or an upload means anything (a member's own firm is implicit otherwise,
  // and the server 400s an upload with no firm_id — see firm_scope.py).
  const needsFirmPicker = isSuperAdmin && !user?.firm
  const [firms, setFirms] = useState<FirmSummary[]>([])
  const [selectedFirmId, setSelectedFirmId] = useState('')

  const [bankStatements, setBankStatements] = useState<BankStatementDoc[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isLoadingStatements, setIsLoadingStatements] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [expandedStatementId, setExpandedStatementId] = useState<string | null>(null)
  const [statementDetails, setStatementDetails] = useState<{ [key: string]: BankStatementDetail }>({})
  const [loadingDetails, setLoadingDetails] = useState<{ [key: string]: boolean }>({})
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState<'uploading' | 'processing' | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchType, setSearchType] = useState<'simple' | 'semantic'>('simple')
  const [isSearching, setIsSearching] = useState(false)
  const [filteredStatements, setFilteredStatements] = useState<BankStatementDoc[]>([])
  const [isFiltered, setIsFiltered] = useState(false)

  useEffect(() => {
    if (!needsFirmPicker) return
    fetchFirms().then((res) => setFirms(res.data.firms)).catch(() => {})
  }, [needsFirmPicker])

  // Load bank statements on mount, and again whenever the super admin picks
  // a different firm.
  useEffect(() => {
    if (needsFirmPicker && !selectedFirmId) {
      setBankStatements([])
      setFilteredStatements([])
      return
    }
    loadBankStatements()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFirmPicker, selectedFirmId])

  const loadBankStatements = async () => {
    setIsLoadingStatements(true)
    try {
      const params = needsFirmPicker ? { firm_id: selectedFirmId } : {}
      const response = await fetchBankStatements(params)
      // A bare list (not wrapped in {statements})
      const statements = (response.data || []) as unknown as BankStatementDoc[]
      setBankStatements(statements)
      setFilteredStatements(statements)
      setIsFiltered(false)
    } catch (error) {
      console.error("Error loading bank statements:", error)
      setErrorMessage(t('bankStatements.list.loadError'))
    } finally {
      setIsLoadingStatements(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredStatements(bankStatements)
      setIsFiltered(false)
      return
    }

    setIsSearching(true)
    setErrorMessage(null)

    try {
      const response = await searchBankStatements(searchQuery, searchType)
      const found = (response.data.statements || []) as unknown as BankStatementDoc[]
      setFilteredStatements(found)
      setIsFiltered(true)

      if (found.length === 0) {
        setErrorMessage(t('bankStatements.search.noResults'))
      }
    } catch (error) {
      console.error("Error searching bank statements:", error)
      setErrorMessage(t('errors.generic'))
      setFilteredStatements(bankStatements)
      setIsFiltered(false)
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery("")
    setFilteredStatements(bankStatements)
    setIsFiltered(false)
    setErrorMessage(null)
  }

  const handleFilesSelected = async (files: File[]) => {
    const file = files[0]
    if (!file) return
    if (needsFirmPicker && !selectedFirmId) return // the drop zone is disabled in this state too

    setIsUploading(true)
    setErrorMessage(null)
    setSuccessMessage(null)
    setUploadProgress(0)
    setUploadStatus('uploading')

    try {
      const form = new FormData()
      form.append('file', file)
      if (needsFirmPicker) form.append('firm_id', selectedFirmId)
      const response = await uploadBankStatement(form, (percentCompleted) => {
        setUploadProgress(percentCompleted)
        // When upload completes, switch to processing status
        if (percentCompleted === 100) {
          setUploadStatus('processing')
        }
      })

      const responseData: UploadResponse = response.data

      if (responseData.status === "success") {
        setSuccessMessage(
          responseData.message ||
          t('bankStatements.upload.success', { count: responseData.transactions_count || 0 })
        )

        if (responseData.warning) {
          setErrorMessage(`⚠️ ${responseData.warning}`)
        }

        // Reload statements to show the new one
        await loadBankStatements()
        // Clear search after successful upload
        clearSearch()
      } else {
        setErrorMessage(responseData.message || t('bankStatements.upload.errorGeneric'))
      }
    } catch (error) {
      console.error("Error uploading bank statement:", error)
      setErrorMessage(`${t('bankStatements.upload.errorGeneric')}: ${(error as any).message || t('errors.generic')}`)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      setUploadStatus(null)
    }
  }

  const handleExpand = async (statementId: string) => {
    if (expandedStatementId === statementId) {
      setExpandedStatementId(null)
      return
    }

    setExpandedStatementId(statementId)

    // Load details if not already loaded
    if (!statementDetails[statementId]) {
      setLoadingDetails(prev => ({ ...prev, [statementId]: true }))
      try {
        const response = await getBankStatement(statementId)
        setStatementDetails(prev => ({ ...prev, [statementId]: response.data as unknown as BankStatementDetail }))
      } catch (error) {
        console.error("Error loading statement details:", error)
        setErrorMessage(t('errors.generic'))
      } finally {
        setLoadingDetails(prev => ({ ...prev, [statementId]: false }))
      }
    }
  }

  const handleDelete = async (statementId: string, fileName: string) => {
    if (!window.confirm(t('bankStatements.list.confirmDelete', { fileName }))) {
      return
    }

    try {
      await deleteBankStatement(statementId)
      setSuccessMessage(t('bankStatements.list.deleteSuccess'))

      // Update both statements lists
      setBankStatements(prev => prev.filter(s => s.id !== statementId))
      setFilteredStatements(prev => prev.filter(s => s.id !== statementId))

      // Remove from details cache if present
      if (statementDetails[statementId]) {
        setStatementDetails(prev => {
          const newDetails = { ...prev }
          delete newDetails[statementId]
          return newDetails
        })
      }

      // Clear any error messages
      setErrorMessage(null)
    } catch (error) {
      console.error("Error deleting bank statement:", error)
      // The client cannot tell in advance whether this user uploaded the
      // statement or administers the firm: the server is the sole judge,
      // and a 403 here means neither is the case.
      const forbidden = axios.isAxiosError(error) && error.response?.status === 403
      setErrorMessage(forbidden ? t('errors.forbidden') : t('bankStatements.list.deleteError'))
    }
  }

  const handleDownload = async (statementId: string) => {
    try {
      await downloadBankStatement(statementId)
    } catch (error) {
      console.error("Error downloading bank statement:", error)
      setErrorMessage(t('bankStatements.list.downloadError'))
    }
  }

  const formatDate = (dateStr: string | null) => (dateStr ? formatDateUtil(dateStr) : "-")

  const formatAmount = (amount: number | null) => (amount === null ? "-" : formatCurrency(amount))

  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop()
    if (ext === 'xml') return <FileCode className="h-4 w-4" />
    if (ext === 'sta') return <FileText className="h-4 w-4" />
    return <FileText className="h-4 w-4" />
  }

  const getFileFormat = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop()
    if (ext === 'xml') return 'CAMT.053'
    if (ext === 'sta') return 'MT940'
    if (ext === 'csv') return 'CSV'
    if (ext === 'pdf') return 'PDF'
    return ext?.toUpperCase() || 'FILE'
  }

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)]">
      {/* Sticky Header */}
      <div className="bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-t-2xl p-6 sticky top-0 z-20">
        <h2 className="text-2xl font-bold mb-4">{t('bankStatements.title')}</h2>

        {needsFirmPicker && (
          <div className="flex items-center gap-2 mb-4">
            <label htmlFor="bank-statements-firm-picker" className="text-sm text-gray-600">
              {t('bankStatements.firmPicker.label')}
            </label>
            <select
              id="bank-statements-firm-picker"
              value={selectedFirmId}
              onChange={(e) => setSelectedFirmId(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              <option value="">{t('bankStatements.firmPicker.placeholder')}</option>
              {firms.map((firm) => (
                <option key={firm.id} value={firm.id}>{firm.name}</option>
              ))}
            </select>
          </div>
        )}

        <DragDropUpload
          onFilesSelected={handleFilesSelected}
          acceptedFileTypes={['.pdf', '.docx', '.doc', '.txt', '.sta', '.xml', '.csv']}
          maxFiles={1}
          disabled={needsFirmPicker && !selectedFirmId}
          label={t('bankStatements.upload.label')}
          customFormatDescription={
            <div className="mt-2">
              <p>{t('bankStatements.upload.formats')}</p>
            </div>
          }
        />
        {needsFirmPicker && !selectedFirmId && (
          <p className="text-sm text-amber-600 mt-2">{t('bankStatements.upload.selectFirmHint')}</p>
        )}

        {isUploading && (
          <div className="mt-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-primary-700">
                  {uploadStatus === 'uploading' ? t('bankStatements.upload.uploading') : t('bankStatements.upload.processing')}
                </span>
                <span className="text-sm font-medium text-primary-700">
                  {uploadStatus === 'uploading' ? `${uploadProgress}%` :
                   <span className="flex items-center gap-2">
                     <Loader2 className="h-4 w-4 animate-spin" />
                     {t('bankStatements.upload.analyzing')}
                   </span>}
                </span>
              </div>
              <div className="w-full bg-primary-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    uploadStatus === 'processing' ? 'bg-primary-700 animate-pulse' : 'bg-primary-700'
                  }`}
                  style={{ width: uploadStatus === 'processing' ? '100%' : `${uploadProgress}%` }}
                />
              </div>
              {uploadStatus === 'processing' && (
                <p className="text-xs text-primary-600 mt-2">
                  {t('bankStatements.upload.processingHint')}
                </p>
              )}
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {errorMessage}
          </div>
        )}

        {/* Search Section */}
        <h3 className="text-xl font-semibold mb-4">{t('bankStatements.search.title')}</h3>

        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={t('bankStatements.search.placeholder')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchType(searchType === 'simple' ? 'semantic' : 'simple')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  searchType === 'semantic'
                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={searchType === 'semantic' ? t('bankStatements.search.semanticTitle') : t('bankStatements.search.simpleTitle')}
              >
                {searchType === 'semantic' ? (
                  <span className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    {t('bankStatements.search.semantic')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    {t('bankStatements.search.simple')}
                  </span>
                )}
              </button>

              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="px-6 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSearching ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('bankStatements.search.searching')}
                  </span>
                ) : (
                  t('common.search')
                )}
              </button>

              {isFiltered && (
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('common.clearSearch')}
                </button>
              )}
            </div>
          </div>

          {searchType === 'semantic' && (
            <p className="text-sm text-purple-600">
              {t('bankStatements.search.semanticHint')}
            </p>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-b-2xl p-6">
        {/* Bank Statements List */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">
            {isFiltered ? t('bankStatements.list.resultsTitle', { count: filteredStatements.length }) : t('bankStatements.list.title')}
          </h3>
          {isFiltered && (
            <span className="text-sm text-gray-500">
              {t('bankStatements.list.searchLabel', { query: searchQuery })}
            </span>
          )}
        </div>

        {needsFirmPicker && !selectedFirmId ? (
          <p className="text-gray-500 text-sm">{t('bankStatements.firmPicker.empty')}</p>
        ) : isLoadingStatements ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : filteredStatements.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            {isFiltered ? t('bankStatements.list.emptyResults') : t('bankStatements.list.empty')}
          </p>
        ) : (
          <div className="space-y-4">
            {filteredStatements.map((statement) => (
              <div key={statement.id} className="border rounded-lg p-4 hover:bg-gray-50">
                {/* Statement Header */}
                <div className="flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => handleExpand(statement.id)}>
                    <h4 className="font-semibold text-lg">{statement.title}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      {statement.bank_name && (
                        <span className="flex items-center gap-1">
                          <Building className="h-4 w-4" />
                          {statement.bank_name}
                        </span>
                      )}
                      {statement.account_number && (
                        <span className="flex items-center gap-1">
                          <CreditCard className="h-4 w-4" />
                          {statement.account_number}
                        </span>
                      )}
                      {(statement.period_start || statement.period_end) && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(statement.period_start)} - {formatDate(statement.period_end)}
                        </span>
                      )}
                      <span className="font-medium">
                        {t('bankStatements.list.transactionsCount', { count: statement.transactions_count })}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                        {getFileIcon(statement.file_name)}
                        {getFileFormat(statement.file_name)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleExpand(statement.id)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={expandedStatementId === statement.id ? t('bankStatements.list.hideDetails') : t('bankStatements.list.showDetails')}
                    >
                      {expandedStatementId === statement.id ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </button>
                    <DocumentActionsDropdown
                      actions={[
                        createCommonActions.download(() => handleDownload(statement.id)),
                        ...(statement.can_delete
                          ? [createCommonActions.delete(() => handleDelete(statement.id, statement.file_name))]
                          : []),
                      ]}
                      showAttachButton={false}
                      position="left"
                    />
                  </div>
                </div>

                {/* Expanded Transactions */}
                {expandedStatementId === statement.id && (
                  <div className="mt-4 border-t pt-4">
                    {loadingDetails[statement.id] ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                      </div>
                    ) : statementDetails[statement.id]?.transactions ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bankStatements.transactions.date')}</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bankStatements.transactions.description')}</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('bankStatements.transactions.category')}</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('bankStatements.transactions.amount')}</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('bankStatements.transactions.balance')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {statementDetails[statement.id].transactions.map((transaction) => (
                              <tr key={transaction.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm">{formatDate(transaction.date)}</td>
                                <td className="px-4 py-2 text-sm">{transaction.description}</td>
                                <td className="px-4 py-2 text-sm">
                                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                    {transaction.category || "-"}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm text-right">
                                  <span className={`flex items-center justify-end gap-1 ${
                                    transaction.type === 'accredito' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {transaction.type === 'accredito' ? (
                                      <TrendingUp className="h-4 w-4" />
                                    ) : (
                                      <TrendingDown className="h-4 w-4" />
                                    )}
                                    {formatAmount(transaction.amount)}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm text-right font-medium">
                                  {formatAmount(transaction.balance)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">{t('bankStatements.transactions.empty')}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
